import { create } from 'zustand';
import toast from 'react-hot-toast';
import { queryClient } from '@/lib/query-client';
import { offlineQueue } from './queue';
import { strategyRegistry } from './sync-strategies';
import { mountOnlineStatusListeners, useOnlineStore, isOnline } from './online-status';
import type { QueueEntry } from './types';

const MAX_RETRIES_BEFORE_REQUIRES_ATTENTION = 3;
const BASE_BACKOFF_MS = 2_000;
const MAX_BACKOFF_MS = 60_000;

interface SyncManagerState {
  /** True while a drain pass is currently running. */
  isSyncing: boolean;
  /** Last successful drain timestamp (epoch ms) — useful for UI. */
  lastSyncedAt: number | null;
  setSyncing: (v: boolean) => void;
  setLastSyncedAt: (v: number) => void;
}

export const useSyncManagerStore = create<SyncManagerState>((set) => ({
  isSyncing: false,
  lastSyncedAt: null,
  setSyncing: (v) => set({ isSyncing: v }),
  setLastSyncedAt: (v) => set({ lastSyncedAt: v }),
}));

function backoffMs(retries: number): number {
  const exp = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** retries);
  // Light jitter so multiple devices don't slam the server simultaneously
  const jitter = Math.floor(Math.random() * 1000);
  return exp + jitter;
}

function invalidateQueriesForResource(resource: QueueEntry['resource']): void {
  if (resource.startsWith('safety.')) {
    void queryClient.invalidateQueries({ queryKey: ['safety'] });
  } else if (resource.startsWith('workforce.')) {
    void queryClient.invalidateQueries({ queryKey: ['workforce'] });
  } else if (resource.startsWith('operations.')) {
    void queryClient.invalidateQueries({ queryKey: ['operations'] });
  }
}

async function processOne(entry: QueueEntry): Promise<'synced' | 'retry' | 'permanent'> {
  const strategy = strategyRegistry[entry.resource];
  if (!strategy) return 'permanent'; // unknown resource — won't fix itself
  if (entry.id === undefined) return 'permanent';

  // Respect backoff window
  if (entry.nextAttemptAt && Date.now() < entry.nextAttemptAt) return 'retry';

  try {
    await offlineQueue.markInFlight(entry.id);
    const payload = strategy.uploadAttachments
      ? await strategy.uploadAttachments(entry)
      : entry.payload;
    await strategy.submit(payload, entry);
    await offlineQueue.markSynced(entry.id);
    invalidateQueriesForResource(entry.resource);
    return 'synced';
  } catch (err) {
    if (strategy.isAlreadyAccepted?.(err)) {
      // Server already has this record (idempotent) — treat as success.
      await offlineQueue.markSynced(entry.id);
      invalidateQueriesForResource(entry.resource);
      return 'synced';
    }
    const message = errMessage(err);
    const isPermanent = strategy.isPermanentFailure?.(err) ?? false;
    const retries = entry.retries + 1;
    const tooMany = retries >= MAX_RETRIES_BEFORE_REQUIRES_ATTENTION;
    const flagAsPermanent = isPermanent || tooMany;
    await offlineQueue.markFailed(entry.id, message, {
      permanent: flagAsPermanent,
      ...(flagAsPermanent ? {} : { nextAttemptAt: Date.now() + backoffMs(retries) }),
    });
    return flagAsPermanent ? 'permanent' : 'retry';
  }
}

function errMessage(err: unknown): string {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e.response?.data?.message ?? e.message ?? 'Sync failed';
}

export const syncManager = {
  /** Boot the manager — call once on app start. */
  start(): void {
    mountOnlineStatusListeners();
    useOnlineStore.subscribe((state, prev) => {
      if (state.isOnline && !prev.isOnline) {
        // Just came back online — try to drain.
        void this.syncNow();
      }
    });
    // Periodic kicker (every 60s) in case `online` event missed in the field.
    if (typeof window !== 'undefined') {
      window.setInterval(() => {
        if (isOnline()) void this.syncNow();
      }, 60_000);
    }
    // Initial drain attempt — covers app refresh while online with queued items.
    if (isOnline()) void this.syncNow();
  },

  /** Drain pending entries once. Safe to call repeatedly. */
  async syncNow(): Promise<{ synced: number; failed: number }> {
    if (!isOnline()) return { synced: 0, failed: 0 };
    if (useSyncManagerStore.getState().isSyncing) return { synced: 0, failed: 0 };
    useSyncManagerStore.getState().setSyncing(true);
    let synced = 0;
    let failed = 0;
    try {
      const pending = await offlineQueue.listPending();
      for (const entry of pending) {
        const result = await processOne(entry);
        if (result === 'synced') synced++;
        else if (result === 'permanent') failed++;
      }
      if (synced > 0) {
        useSyncManagerStore.getState().setLastSyncedAt(Date.now());
        toast.success(`Synced ${synced} record${synced === 1 ? '' : 's'}`);
        await offlineQueue.purgeSynced();
      }
      if (failed > 0) {
        toast.error(`${failed} record${failed === 1 ? '' : 's'} need attention`);
      }
    } finally {
      useSyncManagerStore.getState().setSyncing(false);
    }
    return { synced, failed };
  },

  /** User-initiated retry of a single failed entry. */
  async retryEntry(id: number): Promise<void> {
    await offlineQueue.retry(id);
    await this.syncNow();
  },

  /** User-initiated discard. */
  async discardEntry(id: number): Promise<void> {
    await offlineQueue.discard(id);
  },
};
